import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import {
  MapPin,
  Ruler,
  Money,
  Phone,
  Envelope,
  Calendar,
  MagnifyingGlass,
  Funnel,
  House,
  Tree,
  Tractor,
  CaretDown,
  Plus,
  Heart,
  Eye
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const LAND_CATEGORIES = [
  { value: "all", label: "All Land Types" },
  { value: "agricultural", label: "Agricultural Land" },
  { value: "dairy", label: "Dairy Farm" },
  { value: "tillage", label: "Tillage Land" },
  { value: "mixed", label: "Mixed Farm" },
  { value: "forestry", label: "Forestry" },
  { value: "equestrian", label: "Equestrian" },
  { value: "residential_farm", label: "Residential Farm" },
  { value: "development", label: "Development Land" }
];

const LISTING_TYPES = [
  { value: "all", label: "All Listings" },
  { value: "sale", label: "For Sale" },
  { value: "rent", label: "For Rent" },
  { value: "lease", label: "Long Lease" }
];

const COUNTIES = [
  "All Ireland", "Carlow", "Cavan", "Clare", "Cork", "Donegal", "Dublin",
  "Galway", "Kerry", "Kildare", "Kilkenny", "Laois", "Leitrim", "Limerick",
  "Longford", "Louth", "Mayo", "Meath", "Monaghan", "Offaly", "Roscommon",
  "Sligo", "Tipperary", "Waterford", "Westmeath", "Wexford", "Wicklow",
  "Antrim", "Armagh", "Derry", "Down", "Fermanagh", "Tyrone"
];

export default function LandListingsPage() {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  
  // Filters
  const [category, setCategory] = useState(searchParams.get("category") || "all");
  const [listingType, setListingType] = useState(searchParams.get("type") || "all");
  const [county, setCounty] = useState(searchParams.get("county") || "All Ireland");
  const [minAcres, setMinAcres] = useState(searchParams.get("minAcres") || "");
  const [maxPrice, setMaxPrice] = useState(searchParams.get("maxPrice") || "");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [showFilters, setShowFilters] = useState(false);
  
  const [savedListings, setSavedListings] = useState([]);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 12,
        ...(category !== "all" && { category }),
        ...(listingType !== "all" && { listing_type: listingType }),
        ...(county !== "All Ireland" && { county }),
        ...(minAcres && { min_acres: minAcres }),
        ...(maxPrice && { max_price: maxPrice }),
        ...(searchQuery && { search: searchQuery })
      };
      
      const response = await axios.get(`${API}/land-listings`, { params });
      setListings(response.data.listings);
      setTotalCount(response.data.total);
    } catch (error) {
      console.error("Error fetching land listings:", error);
      toast.error("Failed to load listings");
    } finally {
      setLoading(false);
    }
  }, [page, category, listingType, county, minAcres, maxPrice, searchQuery]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const fetchSavedListings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/saved-land`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedListings(response.data.map(l => l.listing_id));
    } catch (error) {
      console.error("Error fetching saved listings:", error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchSavedListings();
    }
  }, [token, fetchSavedListings]);

  const toggleSave = async (listingId) => {
    if (!token) {
      toast.error("Please sign in to save listings");
      return;
    }
    
    const isSaved = savedListings.includes(listingId);
    try {
      if (isSaved) {
        await axios.delete(`${API}/user/saved-land/${listingId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSavedListings(prev => prev.filter(id => id !== listingId));
        toast.success("Removed from saved");
      } else {
        await axios.post(`${API}/user/saved-land`, 
          { listing_id: listingId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSavedListings(prev => [...prev, listingId]);
        toast.success("Saved to your list");
      }
    } catch (error) {
      toast.error("Failed to update saved listings");
    }
  };

  const applyFilters = () => {
    setPage(1);
    const params = new URLSearchParams();
    if (category !== "all") params.set("category", category);
    if (listingType !== "all") params.set("type", listingType);
    if (county !== "All Ireland") params.set("county", county);
    if (minAcres) params.set("minAcres", minAcres);
    if (maxPrice) params.set("maxPrice", maxPrice);
    if (searchQuery) params.set("q", searchQuery);
    setSearchParams(params);
    fetchListings();
  };

  const formatPrice = (price, type) => {
    if (type === "rent") {
      return `€${price.toLocaleString()}/year`;
    }
    return `€${price.toLocaleString()}`;
  };

  const getCategoryIcon = (cat) => {
    const icons = {
      agricultural: Tractor,
      dairy: House,
      forestry: Tree,
      default: MapPin
    };
    const Icon = icons[cat] || icons.default;
    return <Icon size={16} />;
  };

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEO title="Land for Sale & Rent" description="Browse farmland, dairy farms, tillage land and forestry for sale or rent across Ireland and the UK. List your land free on Farmwatch." url="/land" />
      <Header />
      
      {/* Hero */}
      <section className="bg-gradient-to-r from-[#022C22] to-[#064E3B] py-8">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="px-3 py-1 bg-[#059669] text-white text-xs font-mono uppercase tracking-wider">
              Land
            </span>
            <span className="px-3 py-1 bg-[#FBBF24] text-[#022C22] text-xs font-mono uppercase tracking-wider">
              For Sale & Rent
            </span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-white mb-2">
            Land & Farm Listings
          </h1>
          <p className="text-[#A7F3D0] text-lg">
            Find agricultural land, farms, and forestry across Ireland
          </p>
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="bg-white border border-[#E0D4C8] p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6661]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by location, keywords..."
                className="w-full pl-10 pr-4 py-2 border border-[#E0D4C8] rounded focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
              />
            </div>
            <select
              value={county}
              onChange={(e) => setCounty(e.target.value)}
              className="px-4 py-2 border border-[#E0D4C8] rounded focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
            >
              {COUNTIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-[#E0D4C8] rounded hover:bg-[#F9FAFB]"
            >
              <Funnel size={18} />
              Filters
              <CaretDown size={14} className={`transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>
            <button
              onClick={applyFilters}
              className="px-6 py-2 bg-[#0F766E] text-white font-medium hover:bg-[#064E3B] transition-colors"
            >
              Search
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-[#E0D4C8]">
              <div>
                <label className="block text-xs text-[#6B6661] mb-1">Land Type</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E0D4C8] rounded text-sm"
                >
                  {LAND_CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#6B6661] mb-1">Listing Type</label>
                <select
                  value={listingType}
                  onChange={(e) => setListingType(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E0D4C8] rounded text-sm"
                >
                  {LISTING_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#6B6661] mb-1">Min Acres</label>
                <input
                  type="number"
                  value={minAcres}
                  onChange={(e) => setMinAcres(e.target.value)}
                  placeholder="Any"
                  className="w-full px-3 py-2 border border-[#E0D4C8] rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-[#6B6661] mb-1">Max Price (€)</label>
                <input
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  placeholder="Any"
                  className="w-full px-3 py-2 border border-[#E0D4C8] rounded text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-[#6B6661]">
            {totalCount} {totalCount === 1 ? "listing" : "listings"} found
          </p>
          {user && (
            <Link
              to="/land-listings/create"
              className="flex items-center gap-2 px-4 py-2 bg-[#0F766E] text-white text-sm font-medium hover:bg-[#064E3B] transition-colors"
            >
              <Plus size={18} />
              List Your Land
            </Link>
          )}
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white border border-[#E0D4C8] p-4 animate-pulse">
                <div className="h-48 bg-[#E5E7EB] mb-4"></div>
                <div className="h-6 bg-[#E5E7EB] w-3/4 mb-2"></div>
                <div className="h-4 bg-[#E5E7EB] w-1/2"></div>
              </div>
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12 bg-white border border-[#E0D4C8]">
            <MapPin size={48} className="mx-auto text-[#E0D4C8] mb-4" />
            <h3 className="font-serif text-xl text-[#33302E] mb-2">No listings found</h3>
            <p className="text-[#6B6661]">Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((listing) => (
              <div key={listing.id} className="bg-white border border-[#E0D4C8] group hover:shadow-lg transition-shadow">
                {/* Image */}
                <div className="relative h-48 bg-[#064E3B] overflow-hidden">
                  {listing.images?.[0] ? (
                    <img 
                      src={listing.images[0]} 
                      alt={listing.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <MapPin size={48} className="text-[#0F766E]" />
                    </div>
                  )}
                  
                  {/* Badges */}
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className={`px-2 py-1 text-xs font-medium ${
                      listing.listing_type === "sale" ? "bg-[#0F766E] text-white" :
                      listing.listing_type === "rent" ? "bg-[#FBBF24] text-[#022C22]" :
                      "bg-[#3B82F6] text-white"
                    }`}>
                      {listing.listing_type === "sale" ? "For Sale" :
                       listing.listing_type === "rent" ? "For Rent" : "Long Lease"}
                    </span>
                    {listing.featured && (
                      <span className="px-2 py-1 bg-[#DC2626] text-white text-xs font-medium">
                        Featured
                      </span>
                    )}
                  </div>
                  
                  {/* Save Button */}
                  <button
                    onClick={() => toggleSave(listing.id)}
                    className="absolute top-3 right-3 p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
                  >
                    {savedListings.includes(listing.id) ? (
                      <Heart size={18} weight="fill" className="text-red-500" />
                    ) : (
                      <Heart size={18} className="text-[#6B6661]" />
                    )}
                  </button>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-2 text-xs text-[#6B6661] mb-2">
                    {getCategoryIcon(listing.category)}
                    <span className="capitalize">{listing.category?.replace("_", " ")}</span>
                  </div>
                  
                  <Link to={`/land-listings/${listing.id}`}>
                    <h3 className="font-serif text-lg text-[#33302E] mb-2 line-clamp-2 group-hover:text-[#0F766E] transition-colors">
                      {listing.title}
                    </h3>
                  </Link>
                  
                  <div className="flex items-center gap-2 text-sm text-[#6B6661] mb-3">
                    <MapPin size={14} />
                    <span>{listing.county}, Ireland</span>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-[#E0D4C8]">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1 text-sm">
                        <Ruler size={16} className="text-[#0F766E]" />
                        <span className="font-medium">{listing.acres} acres</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-serif text-xl text-[#0F766E]">
                        {formatPrice(listing.price, listing.listing_type)}
                      </span>
                      {listing.price_per_acre && (
                        <p className="text-xs text-[#6B6661]">
                          €{listing.price_per_acre.toLocaleString()}/acre
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalCount > 12 && (
          <div className="flex justify-center gap-2 mt-8">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-[#E0D4C8] disabled:opacity-50 hover:bg-[#F9FAFB]"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-[#6B6661]">
              Page {page} of {Math.ceil(totalCount / 12)}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= Math.ceil(totalCount / 12)}
              className="px-4 py-2 border border-[#E0D4C8] disabled:opacity-50 hover:bg-[#F9FAFB]"
            >
              Next
            </button>
          </div>
        )}

        {/* Advertise CTA */}
        <div className="mt-12 bg-gradient-to-r from-[#022C22] to-[#064E3B] p-8 text-center">
          <h2 className="font-serif text-2xl text-white mb-3">
            Selling or Renting Land?
          </h2>
          <p className="text-[#A7F3D0] mb-6 max-w-xl mx-auto">
            Reach thousands of farmers across Ireland. Featured listings get 5x more views.
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to="/land-listings/create"
              className="px-6 py-3 bg-white text-[#022C22] font-medium hover:bg-[#F0FDF4] transition-colors"
            >
              List for Free
            </Link>
            <Link
              to="/advertise"
              className="px-6 py-3 border-2 border-white text-white font-medium hover:bg-white/10 transition-colors"
            >
              Featured Listings
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
